import React from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { StageBooking } from "./BookedStageModeCarousel";
import { motion } from "framer-motion";

dayjs.extend(utc);

const StageCardDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const booking = state?.booking as StageBooking;

  if (!booking || String(booking.id) !== String(id)) {
    return (
      <Box px={2} py={4} textAlign="center" fontFamily="Lexend">
        <Typography variant="h6" color="error">
          Booking details not found.
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  const sessionStart = dayjs.utc(booking.start_date_time).local();
  const sessionEnd = dayjs.utc(booking.end_date_time).local();

  const isJoinable =
    booking.status === "Ongoing" ||
    (dayjs().isAfter(sessionStart.subtract(1, "minute")) &&
      dayjs().isBefore(sessionEnd.add(5, "minute")));

  const getStatusStyles = () => {
    switch (booking.status) {
      case "Scheduled":
        return { chip: "#2563EB" };
      case "Pending":
        return { chip: "#F59E0B" };
      case "Ongoing":
        return { chip: "#0D9488" };
      case "Completed":
        return { chip: "#10B981" };
      default:
        return { chip: "#6B7280" };
    }
  };

  const { chip: chipColor } = getStatusStyles();

  return (
    <Box fontFamily="Lexend" sx={{ bgcolor: "#0b0b0f", color: "#fff", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        sx={{
          position: "relative",
          height: isMobile ? "280px" : "420px",
          backgroundImage: `url(${booking.stagecard?.primary_thumbnail})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderBottom: "3px solid rgba(255,255,255,0.15)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.3))",
          }}
        />
        <Stack
          justifyContent="flex-end"
          alignItems={isMobile ? "center" : "flex-start"}
          height="100%"
          px={{ xs: 2, sm: 6 }}
          pb={4}
          spacing={2}
          position="relative"
          zIndex={2}
        >
          <Typography
            variant={isMobile ? "h6" : "h4"}
            fontWeight={800}
            textAlign={isMobile ? "center" : "left"}
          >
            {booking.stagecard?.title || "Stage Event"}
          </Typography>
          <Chip
            label={booking.status}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 12,
              backgroundColor: chipColor,
              color: "#fff",
              boxShadow: "0 0 12px rgba(255,255,255,0.4)",
            }}
          />
        </Stack>
      </Box>

      {/* Content Section */}
      <Box px={{ xs: 2, sm: 6 }} py={4}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#fff",
          }}
        >
          <Stack spacing={2}>
            <DetailRow label="Description" value={booking.stagecard?.short_description || "N/A"} />
            <DetailRow label="Venue" value={booking.venue_name} />
            <DetailRow label="Location" value={booking.venue_location} />
            <DetailRow
              label="Date & Time"
              value={`${sessionStart.format("MMM D, YYYY h:mm A")} – ${sessionEnd.format("h:mm A")}`}
            />
            <DetailRow label="Talent" value={booking.talent?.username || "N/A"} />
            <DetailRow
              label="Message"
              value={
                <Typography fontSize={14} whiteSpace="pre-line" color="rgba(255,255,255,0.85)">
                  {booking.message || "N/A"}
                </Typography>
              }
            />
          </Stack>

          {/* Action Buttons */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            mt={4}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              fullWidth={isMobile}
              sx={{
                fontFamily: "Lexend",
                textTransform: "none",
                fontWeight: 500,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.3)",
                "&:hover": { borderColor: "#fff" },
              }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              disabled={!isJoinable}
              onClick={() =>
                navigate(
                  booking.stagecard?.type === "Sing4You"
                    ? `/sing4you-host/${booking.channel_name}?bookingId=${booking.id}`
                    : `/stage-live-host/${booking.channel_name}?bookingId=${booking.id}`
                )
              }
              fullWidth={isMobile}
              sx={{
                fontFamily: "Lexend",
                textTransform: "none",
                fontWeight: 600,
                background: isJoinable
                  ? "linear-gradient(to right, #e50914, #b81d24)"
                  : "rgba(255,255,255,0.1)",
                color: isJoinable ? "#fff" : "rgba(255,255,255,0.4)",
                "&:hover": {
                  background: isJoinable
                    ? "linear-gradient(to right, #b81d24, #e50914)"
                    : "rgba(255,255,255,0.1)",
                },
              }}
            >
              {isJoinable ? "Join Live Session" : "Join Unavailable"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="start"
    sx={{ flexWrap: "wrap", wordBreak: "break-word" }}
  >
    <Box width={140} minWidth={120}>
      <Typography
        fontWeight={600}
        fontSize={14}
        fontFamily="Lexend"
        color="rgba(255,255,255,0.9)"
      >
        {label}
      </Typography>
    </Box>
    <Box flex={1}>{value}</Box>
  </Stack>
);

export default StageCardDetailsPage;
